import { ChevronLeftOutline, ChevronRightOutline } from '@aurora/icons-react';
import { Button, Dark, SubText } from '@aurora/widgets-react';
import clsx from 'clsx';
import moment from 'moment-timezone';
import React, { useCallback, useRef } from 'react';
import { TimeSlider } from '../TimeSlider';
import { useTranslation } from 'react-i18next';
import styles from './StepControls.scss';

export interface StepControlProps {
  setCurrentTimeIndex: (val: number) => void;
  times: string[];
  currentTimeIndex: number;
  variant: 'primary' | 'secondary';
  format: string;
}

export const StepControls: React.FunctionComponent<StepControlProps> = ({
  times,
  setCurrentTimeIndex,
  currentTimeIndex,
  variant,
  format,
}) => {
  const { t } = useTranslation();

  const timeRef = useRef(currentTimeIndex);
  timeRef.current = currentTimeIndex;
  const next = useCallback(() => {
    const candidate = timeRef.current + 1;
    if (candidate < times.length) setCurrentTimeIndex(candidate);
  }, [times?.length, setCurrentTimeIndex]);
  const prev = useCallback(() => {
    const candidate = timeRef.current - 1;
    if (candidate >= 0) setCurrentTimeIndex(candidate);
  }, [setCurrentTimeIndex]);

  const content = (
    <>
      <Button
        className={styles.stepButton}
        variant={Button.variants.tertiary}
        shape={Button.shapes.round}
        icon={<ChevronLeftOutline />}
        onClick={prev}
        label={t('goBackward')}
        iconOnly={true}
      />
      <Button
        className={styles.stepButton}
        variant={Button.variants.tertiary}
        shape={Button.shapes.round}
        icon={<ChevronRightOutline />}
        onClick={next}
        label={t('goForward')}
        iconOnly={true}
      />
      <div className={styles.currentTime}>
        <SubText className={styles.label}>
          {moment(times[currentTimeIndex]).format(format)}
        </SubText>
        <TimeSlider
          variant={variant}
          times={times}
          value={currentTimeIndex}
          onChange={setCurrentTimeIndex}
        />
      </div>
    </>
  );
  if (variant === 'primary') {
    return (
      <div className={clsx(styles.stepControls, styles.primary)}>{content}</div>
    );
  }
  return (
    <Dark className={clsx(styles.stepControls, styles.secondary)}>
      {content}
    </Dark>
  );
};
